// Multi-manager buyer teams — this team's standings and its tier (Q8 A).
//
// Two reads, both the policy page's own, neither invented here:
//
// 1. STANDINGS — `GET /buyer-allocation/entitlements?buyerTeamId=`, the same
//    computation that feeds the manager's queue and each specialist's widget,
//    narrowed to this roster. 🔴 Standings follow the PERSON (D2): a member's
//    "currently held" is their whole book, including leads that belong to other
//    teams, and the caption says so where the number is read.
//
// 2. THE TEAM TIER — the layer between the global policy and a specialist's
//    override, read off `GET /buyer-allocation/settings` and written through
//    the team-tier endpoints. That read is gated `Buyers.ManageAllocation`
//    server-side, so a caller without it sees the standings and a sentence
//    naming who sets the tier — never a request that would 403.
//
// 🔴 THE EDITOR NEVER RENDERS WHERE A SAVE WOULD FAIL — which is the half that
// matters, and is NOT the same claim as "renders wherever one would succeed".
// The two populations are close and not equal, so the difference is written
// down here rather than left for the next reader to trip over:
//
//   • the SERVER's rule is `Buyers.ManageAllocation` + `scope.IncludesTeam(id)`
//     (`BuyerAllocationAdminService.SaveTeamAsync`), and dispatch scope comes
//     from MEMBERSHIP of any kind;
//   • `team.canManage` (§3.3) additionally requires the Manager role — a
//     team's own `ManagerUserId`, or a member row carrying `RoleInTeam`
//     "Manager".
//
// So a `ManageAllocation` holder who is a member of the team with the
// SPECIALIST role would be accepted by the server and is shown the tier
// read-only. That is the client being NARROWER than the server: a capability
// withheld, never a button that 403s. Whether the server should be narrowed to
// match — the tier is a manager's decision, and today a member can set it — is
// an owner ruling, filed in CLAUDE.md's deferral register. Widening the client
// to match the server is the one change that must NOT be made without it.

import { useState } from "react";
import { Pencil } from "lucide-react";
import { extractErrorMessage } from "@/api/client";
import type { BuyerEntitlementDto, SaveBuyerAllocationPayload } from "@/api/buyers";
import type { BuyerTeamDetailDto } from "@/api/buyerTeams";
import { BuyerAllocationEditorModal } from "@/components/buyers/BuyerAllocationEditorModal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  Button,
  Card,
  Skeleton,
  SubTable,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  useBuyerAllocationSettings,
  useBuyerEntitlements,
  useClearTeamTier,
  useSaveTeamTier,
} from "@/hooks/buyers/useBuyerAllocation";
import { useAuth } from "@/hooks/useAuth";
import { AuthoredRow } from "../BuyerAllocationSettingsPage";
import { InlineQueryError } from "../queryStates";
import { StaleContactDaysField } from "./StaleContactDaysField";

/** The exact string from `Permissions.cs` (`Buyers.ManageAllocation`) — the
 *  gate on the settings read and every tier write. */
export const MANAGE_ALLOCATION_PERMISSION = "Buyers.ManageAllocation";

/** Signed, so a negative gap — legitimate after a manual assign — reads as one
 *  rather than as a count. */
function signed(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return "0";
}

const standingColumns: SubTableColumn<BuyerEntitlementDto>[] = [
  {
    key: "member",
    header: "Member",
    sortAccessor: (e) => e.displayName ?? e.specialistUserId,
    render: (e) => (
      <span className="font-semibold text-onyx">{e.displayName ?? e.specialistUserId}</span>
    ),
  },
  {
    key: "qualifying",
    header: "Qualifying",
    align: "right",
    sortAccessor: (e) => e.qualifyingCount,
    render: (e) => <span className="tabular-nums">{e.qualifyingCount}</span>,
  },
  {
    key: "entitlement",
    header: "Entitlement",
    align: "right",
    sortAccessor: (e) => e.entitlement,
    render: (e) => <span className="tabular-nums">{e.entitlement}</span>,
  },
  {
    key: "held",
    header: "Currently held",
    align: "right",
    sortAccessor: (e) => e.currentlyHeld,
    render: (e) => <span className="tabular-nums">{e.currentlyHeld}</span>,
  },
  {
    key: "gap",
    header: "Gap",
    align: "right",
    sortAccessor: (e) => e.gap,
    render: (e) => <span className="tabular-nums">{signed(e.gap)}</span>,
  },
  {
    key: "towardNext",
    header: "Toward next",
    align: "right",
    sortAccessor: (e) => e.towardNext,
    render: (e) => (
      <span className="tabular-nums">
        {e.towardNext} of {e.qualifyingThreshold}
      </span>
    ),
  },
  {
    key: "cap",
    header: "Cap",
    align: "right",
    sortAccessor: (e) => e.maxActiveBuyers ?? Number.MAX_SAFE_INTEGER,
    // "No cap" is a real state of the policy, not a missing value — muted
    // prose rather than an em dash.
    render: (e) =>
      e.maxActiveBuyers == null ? (
        <span className="text-gray-mid">No cap</span>
      ) : (
        <span className="tabular-nums">{e.maxActiveBuyers}</span>
      ),
  },
];

export function AllocationTab({ team }: { team: BuyerTeamDetailDto }) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canReadTier = hasPermission(MANAGE_ALLOCATION_PERMISSION);
  const canEditTier = canReadTier && team.canManage === true;

  const [editing, setEditing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const standings = useBuyerEntitlements(team.id);
  const settings = useBuyerAllocationSettings(canReadTier);
  const save = useSaveTeamTier(team.id);
  const clear = useClearTeamTier(team.id);

  // 🔴 Intersected with the roster on purpose. The server narrows the read to
  // this team's roster for an all-teams caller and IGNORES the narrowing for
  // anybody else — their own team stands — so a one-team manager opening
  // another team's page would otherwise be shown their own people under this
  // team's heading. A standing for somebody not on this roster is never this
  // team's, whatever the request said.
  const onRoster = new Set(team.members.map((m) => m.userId));
  const rows = (standings.data ?? []).filter((e) => onRoster.has(e.specialistUserId));

  const tier = settings.data?.teamTiers?.find((t) => t.buyerTeamId === team.id);
  const effective = tier?.effective ?? settings.data?.effectiveDefaults;
  const authored = tier?.authored;
  const tierAuthored =
    authored != null &&
    (authored.startingAllocation != null ||
      authored.qualifyingThreshold != null ||
      authored.maxActiveBuyers != null ||
      authored.notes != null ||
      authored.staleContactDays != null);

  // 🔴 The shared editor modal knows nothing about the stale threshold (it is
  // also the specialist override's editor, where the server refuses the
  // field), so every save it produces is re-merged with what this tier
  // already authored — otherwise saving the three numbers would silently
  // clear the fourth. The stale field's own save does the inverse.
  function withStale(body: SaveBuyerAllocationPayload, staleContactDays: number | null): SaveBuyerAllocationPayload {
    return { ...body, staleContactDays };
  }
  function authoredBody(): SaveBuyerAllocationPayload {
    return {
      startingAllocation: authored?.startingAllocation ?? null,
      qualifyingThreshold: authored?.qualifyingThreshold ?? null,
      maxActiveBuyers: authored?.maxActiveBuyers ?? null,
      notes: authored?.notes ?? null,
    };
  }

  function confirmReset() {
    clear.mutate(undefined, {
      onSuccess: () => {
        setResetting(false);
        toast.success(`"${team.name}" follows the global policy again.`);
      },
      onError: (e) => {
        setResetting(false);
        toast.error(extractErrorMessage(e));
      },
    });
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-allocation-tab">
      <Card>
        <h2 className="font-display text-h3 text-onyx m-0">Standings</h2>
        {/* The D2 rule, stated where the numbers it governs are read. */}
        <p className="text-caption text-gray-mid m-0 mt-space-1 mb-space-3 max-w-reading">
          Standings follow the PERSON: a member&apos;s held count includes leads belonging to
          other teams.
        </p>

        {standings.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : standings.isError ? (
          <InlineQueryError
            what="this team's standings"
            onRetry={() => void standings.refetch()}
            retrying={standings.isFetching}
          />
        ) : (
          <SubTable<BuyerEntitlementDto>
            caption={`Standings for ${team.name}`}
            data-testid="buyer-team-standings"
            rows={rows}
            rowKey={(e) => e.specialistUserId}
            columns={standingColumns}
            searchPlaceholder="Search members…"
            empty={{
              message: "Nobody on this team has a standing yet.",
              door: {
                kind: "derived",
                explain:
                  "A standing appears for each member who holds a buyer role. Members are added on the Members tab.",
              },
            }}
          />
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-space-3">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-onyx m-0">Team tier</h2>
            <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-reading">
              The layer between the global policy and a consultant&apos;s own override. Anything
              left on inherit follows the global policy.
            </p>
          </div>
          {canEditTier && settings.data && (
            <div className="flex items-center gap-space-2">
              {tierAuthored && (
                <Button
                  size="sm"
                  variant="text"
                  onClick={() => setResetting(true)}
                  disabled={clear.isPending}
                  data-testid="buyer-team-tier-reset"
                >
                  Reset to global policy
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditing(true)}
                data-testid="buyer-team-tier-edit"
              >
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit team tier
              </Button>
            </div>
          )}
        </div>

        {!canReadTier ? (
          /* No request: the settings read is gated on the same permission as
             the write, so a caller without it is told who sets the tier rather
             than shown a request that would 403. */
          <p className="text-small text-gray-mid m-0 mt-space-3" data-testid="buyer-team-tier-readonly">
            The tier is read and set by buyer managers who hold the allocation policy.
          </p>
        ) : settings.isLoading ? (
          <Skeleton className="h-24 w-full mt-space-3" />
        ) : settings.isError || !settings.data || !effective ? (
          <div className="mt-space-3">
            <InlineQueryError
              what="this team's tier"
              onRetry={() => void settings.refetch()}
              retrying={settings.isFetching}
            />
          </div>
        ) : (
          <div
            className="mt-space-3 border border-border-light rounded-md bg-white-warm p-space-4 flex flex-col gap-space-3"
            data-testid="buyer-team-tier"
          >
            <AuthoredRow
              label="Starting allocation"
              authored={authored?.startingAllocation}
              effective={effective.startingAllocation}
            />
            <AuthoredRow
              label="Qualifying threshold"
              authored={authored?.qualifyingThreshold}
              effective={effective.qualifyingThreshold}
            />
            <AuthoredRow
              label="Cap on active buyers"
              authored={authored?.maxActiveBuyers}
              effective={effective.maxActiveBuyers}
              isCap
            />
            {/* D10 — the team's stale window: what "no contact" means on this
                team's board, list and digest. Authored here or on the global
                row, never on a specialist. */}
            <StaleContactDaysField
              authored={authored?.staleContactDays}
              effective={tier?.staleContactDays}
              canEdit={canEditTier}
              saving={save.isPending}
              onSave={(days) =>
                save.mutate(withStale(authoredBody(), days), {
                  onSuccess: () => toast.success(`Stale window for "${team.name}" saved.`),
                  onError: (e) => toast.error(extractErrorMessage(e)),
                })
              }
              testIdPrefix="buyer-team-tier"
            />
            {authored?.notes && <p className="text-caption text-gray-mid m-0">{authored.notes}</p>}
            {!canEditTier && (
              <p className="text-caption text-gray-mid m-0" data-testid="buyer-team-tier-readonly">
                Read-only here: this team&apos;s tier is edited by its manager, or by someone
                with all-teams authority.
              </p>
            )}
          </div>
        )}
      </Card>

      {editing && settings.data && effective && (
        <BuyerAllocationEditorModal
          open
          onClose={() => {
            setEditing(false);
            setSaveError(null);
          }}
          title="Team tier"
          subject={team.name}
          inherited={settings.data.effectiveDefaults}
          inheritedLabel="the global policy"
          values={authored}
          saving={save.isPending}
          error={saveError}
          onSave={(body) =>
            save.mutate(withStale(body, authored?.staleContactDays ?? null), {
              onSuccess: () => {
                setEditing(false);
                setSaveError(null);
                toast.success(`Tier for "${team.name}" saved.`);
              },
              // Surfaced verbatim: the server owns the rules and its message
              // already says what to do about it.
              onError: (e) => setSaveError(extractErrorMessage(e)),
            })
          }
        />
      )}

      {resetting && (
        <ConfirmModal
          open
          title="Reset to the global policy?"
          message={
            <>
              Removes every value authored on <strong>{team.name}</strong>&apos;s tier. Its
              members follow the global policy again; their own per-consultant overrides are
              left alone.
            </>
          }
          confirmLabel="Reset"
          destructive
          onConfirm={confirmReset}
          onCancel={() => setResetting(false)}
        />
      )}
    </div>
  );
}
